import { HighlightDroppable } from "@/components/forms/components/HighlightDroppable.tsx";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Tag,
  Text,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  CustomDraggableItemProps,
  CustomDraggableNodeType,
} from "@/pages/settings/custom-fields/custom-fields.ts";
import { CustomFieldEditModal } from "@/pages/settings/custom-fields/widgets/CustomFieldEditModal.tsx";
import { CustomSectionEditModal } from "@/pages/settings/custom-fields/widgets/CustomSectionEditModal.tsx";
import {
  ApiCFEntityTypes,
  ApiField,
  ApiFieldTypes,
  ApiSection,
  ApiSectionTypes,
  ApiTable,
  ApiTableTypes,
} from "@/types/custom-field.types.ts";
import { Draggable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useSectionSave, useTableById, useTableDelete } from "@/queries/use-custom-fields.query.ts";
import { EmployeeCustomTableFieldEditModal } from "@/pages/settings/custom-fields/widgets/EmployeeCustomTableFieldEditModal.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";

export function CustomDraggableItem({
  node,
  index,
  children = [],
  draggingSection,
  draggingMandatoryField,
  tabType,
  table,
}: CustomDraggableItemProps) {
  const { t } = useTranslation();
  const { getFieldTypeInformation } = useCustomFieldsHook();

  if (node.type === CustomDraggableNodeType.section) {
    return (
      <CustomDraggableSectionItem
        node={node}
        index={index}
        name={t("common:section")}
        children={children}
        draggingSection={draggingSection}
        draggingMandatoryField={draggingMandatoryField}
        tabType={tabType}
      />
    );
  }

  if (node.type === CustomDraggableNodeType.table) {
    const table = node.data as ApiTable;
    const name =
      table.table_type == ApiTableTypes.custom
        ? t("custom_fields:custom_table")
        : t("custom_fields:system_table");

    return <CustomDraggableTableItem node={node} index={index} name={name} />;
  }

  const field = node.data as ApiField;
  const name = getFieldTypeInformation(field.field_type, field.type).title;

  return <CustomDraggableFieldItem node={node} index={index} name={name} table={table} />;
}

function CustomDraggableSectionItem({
  node,
  index,
  children,
  draggingSection,
  draggingMandatoryField,
  name,
  tabType,
}: CustomDraggableItemProps & { name: string }) {
  const { t } = useTranslation();
  const { getSectionTitle } = useCustomFieldsHook();
  const { openModal } = useModals();
  const { isFeatureEnabled } = usePermissions();
  const section = node.data as ApiSection;
  const sectionSave = useSectionSave();
  const canManageCustomFields = isFeatureEnabled(ApiFeatureType.custom_fields);

  const handleEdit = () => {
    if (!section) return;
    if (!tabType) return;
    openModal(CustomSectionEditModal, { section, tabType: tabType });
  };

  const canToggleHidden =
    (section.entity_type !== ApiCFEntityTypes.employee &&
      // TODO: вернуть скрытие секций кандидата
      section.entity_type !== ApiCFEntityTypes.applicant &&
      (section.section_type !== ApiSectionTypes.custom || canManageCustomFields)) ||
    [
      ApiSectionTypes.employee_overview_summary,
      ApiSectionTypes.employee_overview_teams,
      ApiSectionTypes.employee_overview_direct_reports,
      ApiSectionTypes.employee_overview_department_employees,
      ApiSectionTypes.employee_overview_recognitions,
      ApiSectionTypes.employee_basic_info_recognitions,
      ApiSectionTypes.employee_basic_info_hired_via_job,
    ].includes(section.section_type);

  const hidesJobAi =
    !section.is_hidden &&
    isFeatureEnabled(ApiFeatureType.ats_ai) &&
    children?.some(
      (child) =>
        child.type === CustomDraggableNodeType.field &&
        (child.data as ApiField).type === ApiFieldTypes.job_description &&
        !(child.data as ApiField).is_hidden
    );

  const handleToggleHidden = () => {
    if (!section) return;
    if (!tabType) return;
    const params = {
      id: section.id,
      payload: {
        tab_type: tabType,
        title: section.title,
        entity_type: section.entity_type,
        is_hidden: !section.is_hidden,
      },
    };

    if (!hidesJobAi) {
      sectionSave.mutate(params);
      return;
    }

    openModal(ConfirmationModal, {
      question: t("custom_fields:job_description_hidden_ai_title"),
      text: t("custom_fields:job_description_hidden_ai_description"),
      acceptTitle: t("common:hide"),
      onAccept: async () => {
        await sectionSave.mutateAsync(params);
      },
    });
  };

  return (
    <Draggable draggableId={`section-${node.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <div {...provided.dragHandleProps}>
            <Stack
              className={`py-3 border-b border-dark/10`}
              direction={"horizontal"}
              gap={"xs"}
              items={"baseline"}
            >
              <Stack
                className={"w-8 h-8 rounded-sm hover:bg-dark/5 cursor-move shrink-0"}
                items={"center"}
                justify={"center"}
              >
                <FontAwesomeIcon icon="fa-light fa-folder" />
              </Stack>
              <Stack className={"grow shrink-0"}>
                <Title
                  header={getSectionTitle(section)}
                  size="xs"
                  className={section.is_hidden ? "line-through text-dark/60" : "text-dark/95"}
                />
              </Stack>
              <Stack className={"w-1/6 shrink-0"}>
                <Text className={"text-secondary"}>{name}</Text>
              </Stack>
              <Stack className={"w-1/5"} justify={"end"} direction={"horizontal"} gap={"sm"}>
                {section.section_type == ApiSectionTypes.custom && canManageCustomFields && (
                  <Button
                    variant={"secondary"}
                    onClick={(event) => {
                      handleEdit();
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                  >
                    {t("common:edit")}
                  </Button>
                )}
                {canToggleHidden && (
                  <Button
                    variant={"secondary"}
                    onClick={(event) => {
                      handleToggleHidden();
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                  >
                    {section.is_hidden ? t("common:show") : t("common:hide")}
                  </Button>
                )}
              </Stack>
            </Stack>
          </div>

          <HighlightDroppable
            droppableId={`section-${node.id}`}
            type="field"
            isDropDisabled={section.is_hidden && draggingMandatoryField}
            className={"rounded-none"}
          >
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {children?.map((field, fieldIndex) => (
                  <CustomDraggableItem
                    key={`field-${field.id}`}
                    node={field}
                    index={fieldIndex}
                    draggingSection={draggingSection}
                    tabType={tabType}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </HighlightDroppable>
        </div>
      )}
    </Draggable>
  );
}

export function CustomDraggableFieldItem({
  node,
  index,
  name,
  table: providedTable,
}: Omit<CustomDraggableItemProps, "tabType"> & { name: string }) {
  const field = node.data as ApiField;
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { getFieldTitle } = useCustomFieldsHook();
  const { isFeatureEnabled } = usePermissions();
  const canEditField = field.is_system || isFeatureEnabled(ApiFeatureType.custom_fields);

  const getFieldTitleExtended = (field: ApiField) => {
    if (
      table?.table_type === ApiTableTypes.employee_terminations &&
      field.type === ApiFieldTypes.effective_from
    ) {
      return field.title?.trim() || t("core:termination_date");
    }
    return getFieldTitle(field);
  };

  const enableTableLookup =
    field.entity_type === ApiCFEntityTypes.employee_table && field.table_id !== 0;

  const { table: loadedTable } = useTableById(
    enableTableLookup && !providedTable ? field.table_id! : 0
  );
  const table = providedTable ?? loadedTable;

  const handleEdit = () => {
    if (!field) return;

    if (field.entity_type != ApiCFEntityTypes.employee_table) {
      openModal(CustomFieldEditModal, { field });
    } else {
      if (!table) return;
      openModal(EmployeeCustomTableFieldEditModal, { field, table });
    }
  };

  return (
    <Draggable draggableId={`field-${node.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <Stack
            className={`py-3 border-b border-dark/10`}
            direction={"horizontal"}
            gap={"xs"}
            items={"baseline"}
          >
            <Stack
              className={"w-8 h-8 rounded-sm hover:bg-dark/5 cursor-move shrink-0"}
              items={"center"}
              justify={"center"}
            >
              <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
            </Stack>
            <Stack className={"shrink-0 grow"} direction="horizontal" gap="sm" items="center">
              <Text className={field.is_hidden ? "font-semibold text-secondary" : "font-semibold"}>
                {getFieldTitleExtended(field)}
              </Text>
              {field.is_hidden && (
                <Tag
                  variant="secondary"
                  appearance="filled"
                  className="min-w-0 border-dark/10 bg-dark/10 px-1.5 py-0.5 text-xs leading-none text-dark/60"
                >
                  {t("custom_fields:badge_hidden")}
                </Tag>
              )}
              {field.is_mandatory && (
                <Tag
                  variant="secondary"
                  appearance="filled"
                  className="min-w-0 border-dark/10 bg-dark/10 px-1.5 py-0.5 text-xs leading-none text-dark/60"
                >
                  {t("custom_fields:badge_mandatory")}
                </Tag>
              )}
              {field.is_system && (
                <Tag
                  variant="secondary"
                  appearance="filled"
                  className="min-w-0 border-dark/10 bg-dark/10 px-1.5 py-0.5 text-xs leading-none text-dark/60"
                >
                  {t("custom_fields:badge_system")}
                </Tag>
              )}
              {field.is_calculated && (
                <Tag
                  variant="secondary"
                  appearance="filled"
                  className="min-w-0 border-dark/10 bg-dark/10 px-1.5 py-0.5 text-xs leading-none text-dark/60"
                >
                  {t("custom_fields:badge_calculated")}
                </Tag>
              )}
            </Stack>
            <Stack className={"w-1/6 shrink-0"}>
              <Text className={"text-secondary"}>{name}</Text>
            </Stack>
            <Stack className={"w-1/5"} justify={"end"} gap={"sm"} direction={"horizontal"}>
              {canEditField && (
                <Button
                  variant={"secondary"}
                  onClick={(event) => {
                    handleEdit();
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                >
                  {t("common:edit")}
                </Button>
              )}
            </Stack>
          </Stack>
        </div>
      )}
    </Draggable>
  );
}

export function CustomDraggableTableItem({
  node,
  index,
  name,
}: Omit<CustomDraggableItemProps, "tabType"> & { name: string }) {
  const table = node.data as ApiTable;
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const { getTableTitle } = useCustomFieldsHook();
  const { isFeatureEnabled } = usePermissions();
  const canManageCustomFields = isFeatureEnabled(ApiFeatureType.custom_fields);

  const { mutateAsync: tableDelete } = useTableDelete();
  const { showInfo } = useToasts();

  const handleEdit = () => {
    if (!table) return;
    navigate(`/settings/custom-fields/table/${table.id}`);
  };

  const handleDelete = () => {
    if (!table) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          const result = await tableDelete(table.id);

          if (result) showInfo({ title: t("custom_fields:table_deleted") });

          analyticsService.trackEvent(analyticEvents.customFields.tableDeleted, {
            [analyticProperties.id]: table.id,
          });
        } catch {
          return;
        }
      },
    });
  };

  return (
    <Draggable draggableId={`table-${node.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <Stack
            className={`py-3 border-b border-dark/10`}
            direction={"horizontal"}
            gap={"xs"}
            items={"baseline"}
          >
            <Stack
              className={"w-8 h-8 rounded-sm hover:bg-dark/5 cursor-move shrink-0"}
              items={"center"}
              justify={"center"}
            >
              <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
            </Stack>
            <Stack className={"shrink-0 grow font-semibold"}>{getTableTitle(table)}</Stack>
            <Stack className={"w-1/6 shrink-0"}>
              <Text className={"text-secondary"}>{name}</Text>
            </Stack>
            <Stack className={"w-1/5"} justify={"end"} gap={"sm"} direction={"horizontal"}>
              <Button
                variant={"secondary"}
                onClick={(event) => {
                  handleEdit();
                  event.stopPropagation();
                  event.preventDefault();
                }}
              >
                {t("common:edit")}
              </Button>
              {table.table_type == ApiTableTypes.custom && canManageCustomFields && (
                <>
                  <DropdownMenu>
                    <DropdownMenuItem
                      onClick={(event) => {
                        navigate(`/employees/table/${table.slug}/rows`);
                        event.stopPropagation();
                        event.preventDefault();
                      }}
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-table-list"} />}
                      title={t("custom_fields:view_all_data")}
                      description={t("custom_fields:for_all_employees")}
                    />
                    <DropdownMenuItem
                      onClick={(event) => {
                        handleDelete();
                        event.stopPropagation();
                        event.preventDefault();
                      }}
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                      variant={"menuDanger"}
                      title={t("common:delete") + "..."}
                    />
                  </DropdownMenu>
                </>
              )}
            </Stack>
          </Stack>
        </div>
      )}
    </Draggable>
  );
}
